// Ativar/desativar a verificação em duas etapas (TOTP), sem bibliotecas de QR: mostra o segredo e o link otpauth.
import { useState, type FormEvent } from 'react'
import { ShieldCheck, ShieldOff } from 'lucide-react'
import type { TotpSetupResponse } from '@impulso/shared/api/identity'
import { api, errorMessage } from '../../lib/api'
import { useSession } from '../../lib/session'
import { useToast } from '../../ui/toast'
import { FormError } from './AuthShell'
import PasswordInput from './PasswordInput'

const codeInput = (v: string) => v.replace(/\D/g, '').slice(0, 6)

export default function TwoFactorCard() {
  const { me, refresh } = useSession()
  const toast = useToast()
  const [setup, setSetup] = useState<TotpSetupResponse | null>(null)
  const [code, setCode] = useState('')
  const [password, setPassword] = useState('')
  const [disabling, setDisabling] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const enabled = !!me?.user.totpEnabled

  async function run(fn: () => Promise<void>) {
    setError(null)
    setBusy(true)
    try { await fn() } catch (err) { setError(errorMessage(err)) } finally { setBusy(false) }
  }

  const start = () => run(async () => { setSetup(await api.post<TotpSetupResponse>('/auth/2fa/setup')); setCode('') })
  const enable = (e: FormEvent) => { e.preventDefault(); return run(async () => {
    await api.post('/auth/2fa/enable', { code })
    setSetup(null); setCode('')
    await refresh()
    toast('Verificação em duas etapas ativada.')
  }) }
  const disable = (e: FormEvent) => { e.preventDefault(); return run(async () => {
    await api.post('/auth/2fa/disable', { password, code })
    setDisabling(false); setCode(''); setPassword('')
    await refresh()
    toast('Verificação em duas etapas desativada.')
  }) }

  return (
    <div className="card">
      <div className="card-head">
        <span className={`ic ${enabled ? 'green' : 'amber'}`}>{enabled ? <ShieldCheck size={18} /> : <ShieldOff size={18} />}</span>
        <div>
          <h2>Verificação em duas etapas</h2>
          <div className="small muted">Além da senha, pede um código do seu celular ao entrar.</div>
        </div>
        <span className={`badge ${enabled ? 'green' : ''}`} style={{ marginLeft: 'auto' }}>{enabled ? 'Ativada' : 'Desativada'}</span>
      </div>
      <FormError error={error} />

      {!enabled && !setup && (
        <div className="stack" style={{ gap: 10 }}>
          <p className="small muted">Você vai precisar de um aplicativo autenticador no celular, como Google Authenticator, Microsoft Authenticator ou Authy.</p>
          <button className="btn primary" style={{ alignSelf: 'flex-start' }} disabled={busy} onClick={start}>Ativar</button>
        </div>
      )}

      {!enabled && setup && (
        <form className="stack" style={{ gap: 12 }} onSubmit={enable}>
          <ol className="small" style={{ margin: 0, paddingLeft: 18, lineHeight: 1.7 }}>
            <li>No aplicativo autenticador, toque em “adicionar conta” e escolha <b>digitar uma chave</b>.</li>
            <li>Use o nome <b>Seu Impulso</b> e esta chave:</li>
          </ol>
          <code style={{ display: 'block', padding: '10px 12px', background: 'var(--surface-2)', border: '1px solid var(--border)', borderRadius: 10, fontSize: 15, letterSpacing: '0.08em', wordBreak: 'break-all' }}>
            {setup.secret.match(/.{1,4}/g)?.join(' ')}
          </code>
          <p className="small muted">No celular? <a className="strong" style={{ color: 'var(--primary)' }} href={setup.otpauthUrl}>Abrir direto no aplicativo</a>.</p>
          <div className="field">
            <label htmlFor="totp-enable">3. Digite o código de 6 números que aparece no aplicativo</label>
            <input id="totp-enable" className="input" inputMode="numeric" autoComplete="one-time-code" value={code} onChange={(e) => setCode(codeInput(e.target.value))} style={{ maxWidth: 180, letterSpacing: '0.3em' }} />
          </div>
          <div className="row">
            <button className="btn primary" disabled={busy || code.length !== 6}>Confirmar e ativar</button>
            <button type="button" className="btn ghost" onClick={() => setSetup(null)}>Cancelar</button>
          </div>
        </form>
      )}

      {enabled && !disabling && <button className="btn" disabled={busy} onClick={() => { setDisabling(true); setCode('') }}>Desativar</button>}
      {enabled && disabling && (
        <form className="stack" style={{ gap: 12 }} onSubmit={disable}>
          <p className="small muted">Para desativar, confirme sua senha e um código do aplicativo.</p>
          <div className="field"><label htmlFor="totp-pass">Senha</label><PasswordInput id="totp-pass" value={password} onChange={setPassword} autoComplete="current-password" /></div>
          <div className="field">
            <label htmlFor="totp-off">Código do aplicativo</label>
            <input id="totp-off" className="input" inputMode="numeric" autoComplete="one-time-code" value={code} onChange={(e) => setCode(codeInput(e.target.value))} style={{ maxWidth: 180, letterSpacing: '0.3em' }} />
          </div>
          <div className="row">
            <button className="btn primary" disabled={busy || code.length !== 6}>Desativar</button>
            <button type="button" className="btn ghost" onClick={() => setDisabling(false)}>Cancelar</button>
          </div>
        </form>
      )}
    </div>
  )
}
